import React, { Component } from "react";
import { connect } from "react-redux";
import SearchRecord from "../../UI/TableRecord/SearchRecord";
import Pagination from "../../UI/TableRecord/Pagination";
import CardBox from "../../UI/CardBox";
import Icons from "../../Icons/Icons";
import PagesServices from "../../../services/PagesServices";
import http from "../../../services/http";
import * as actions from "../../../store/actions";
import {
  buildLookupExecutePayload,
  extractConfigAttributes,
} from "../../../Containers/PagesContainer/PagesContainerUtils";
import {
  buildListViewColumnsPrefKey,
  buildListViewMassLoopData,
  columnsToCardBoxData,
  extractListViewExecuteResult,
  extractListViewMediaUrl,
  formatListViewCellValue,
  getListViewMassActionNodes,
  getListViewPageIds,
  getListViewRowActionNodes,
  getListViewSelectedCount,
  getListViewToolbarNodes,
  isListViewActionColumnEnabled,
  isListViewPageFullySelected,
  isListViewRowSelected,
  normalizeListViewFieldType,
  isListViewCheckboxFieldType,
  isListViewCurrencyFieldType,
  parseListViewCurrencyParts,
  getRowIdentity,
  getValueByPath,
  loadListViewColumnPrefs,
  mergeColumnPreferences,
  reorderColumns,
  resolveListViewInstanceId,
  resolveListViewUserId,
  saveListViewColumnPrefs,
  toggleColumnVisibility,
  toggleListViewSelectedId,
} from "./ScmListViewUtils";
import "./ScmListViewField.css";

/**
 * scm:listview — generic Pages list chrome (Header Column, Search, Showing range, Pagination).
 * Data via Page Configuration execute (Steps / Ratings / API). Local state only; no Redux tableRecords.
 */
class ScmListViewField extends Component {
  constructor(props) {
    super(props);
    const listviewId = resolveListViewInstanceId(props.attributes || {});
    this.boxId = `scmListViewCols_${listviewId}`.replace(/[^a-zA-Z0-9_-]/g, "_");
    this.requestSeq = 0;
    this._isMounted = false;
    this.rootRef = React.createRef();
    this.hostPort = null;

    this.state = {
      listviewId,
      config: null,
      pageConfig: null,
      pageConfigId: null,
      inputVariables: [],
      columns: [],
      draftColumns: [],
      rows: [],
      page: 0,
      perPage: 10,
      search: "",
      totalRecords: 0,
      loading: false,
      error: null,
      headerColumnOpen: false,
      initialized: false,
      selectedIds: [],
      isSelectAll: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.pinLayoutToViewport();
    window.addEventListener("resize", this.pinLayoutToViewport);
    this.bootstrap();
  }

  componentDidUpdate() {
    this.pinLayoutToViewport();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.requestSeq += 1;
    this.setPageLoader(false);
    window.removeEventListener("resize", this.pinLayoutToViewport);
    if (this.hostPort) {
      this.hostPort.classList.remove("scm-listview-host");
      this.hostPort = null;
    }
  }

  setPageLoader = (bool) => {
    if (typeof this.props.Loader === "function") {
      this.props.Loader(bool);
    }
  };

  /**
   * Fill remaining Pages viewport so Header Column stays at the top and
   * pagination stays at the bottom. Only the table body scrolls.
   */
  pinLayoutToViewport = () => {
    const el = this.rootRef.current;
    if (!el) return;

    const port = el.closest(".outerPort") || el.closest(".viewport");
    if (port && port !== this.hostPort) {
      if (this.hostPort) {
        this.hostPort.classList.remove("scm-listview-host");
      }
      this.hostPort = port;
      this.hostPort.classList.add("scm-listview-host");
    }

    const boundsEl = this.hostPort || el.parentElement;
    if (!boundsEl) return;
    const bottom = boundsEl.getBoundingClientRect().bottom;
    const top = el.getBoundingClientRect().top;
    const nextHeight = Math.max(280, Math.floor(bottom - top));
    const next = `${nextHeight}px`;
    if (el.style.height !== next) {
      el.style.height = next;
    }
  };

  getChildrenNodes = () => {
    const { elmtParams } = this.props;
    return elmtParams?.children || [];
  };

  getFieldFormPayload = () => {
    return this.props.elmtParams?.defProps?.fieldFormPayload || {};
  };

  bootstrap = async () => {
    const config = extractConfigAttributes(this.getChildrenNodes());
    if (!config || !config.apiAction || String(config.apiAction).trim() === "") {
      if (this._isMounted) {
        this.setState({
          error: "scm:listview requires scm:config with a valid action (Page Configuration API name).",
          initialized: true,
        });
      }
      return;
    }

    const columns = this.loadColumnsForConfig(config);
    if (this._isMounted) {
      this.setState({ config, columns, draftColumns: columns.map((c) => ({ ...c })) });
    }

    try {
      await this.fetchPageConfigAndVariables(config.apiAction);
      if (!this._isMounted) return;
      await this.executeList(
        {
          page: 0,
          perPage: this.state.perPage,
          search: this.state.search,
        },
        config
      );
    } catch (e) {
      if (this._isMounted) {
        this.setState({
          error: "Failed to load Page Configuration for this list.",
          loading: false,
          initialized: true,
        });
      }
    }
  };

  loadColumnsForConfig = (config) => {
    const prefKey = buildListViewColumnsPrefKey(
      resolveListViewUserId(),
      config.apiAction,
      this.state.listviewId
    );
    const saved = loadListViewColumnPrefs(prefKey);
    return mergeColumnPreferences(config.resultFields || [], saved);
  };

  fetchPageConfigAndVariables = async (apiAction) => {
    const pageConfigResponse = await PagesServices.getConfigByName(apiAction);
    if (!pageConfigResponse || pageConfigResponse.code !== 200 || !pageConfigResponse.data) {
      throw new Error("page_config_not_found");
    }
    const pageConfig = pageConfigResponse.data;
    const pageConfigId = pageConfig.id;
    const variablesResponse = await PagesServices.getConfigVariablesList(
      pageConfigId,
      apiAction
    );
    if (!variablesResponse || variablesResponse.code !== 200 || !variablesResponse.data) {
      throw new Error("page_variables_not_found");
    }
    const content = variablesResponse.data.content || [];
    const inputVariables = content.filter((v) => v.is_request === true);

    if (this._isMounted) {
      this.setState({ pageConfig, pageConfigId, inputVariables });
    }
    return { pageConfig, pageConfigId, inputVariables };
  };

  executeList = async (paginationOverride = {}, configOverride = null) => {
    const config = configOverride || this.state.config;
    if (!config || !config.apiAction) return;

    const page = paginationOverride.page != null ? paginationOverride.page : this.state.page;
    const perPage =
      paginationOverride.perPage != null ? paginationOverride.perPage : this.state.perPage;
    const search =
      paginationOverride.search != null ? paginationOverride.search : this.state.search;

    const seq = ++this.requestSeq;
    this.setPageLoader(true);
    if (this._isMounted) {
      this.setState({
        loading: true,
        error: null,
        page,
        perPage,
        search,
        selectedIds: [],
        isSelectAll: false,
      });
    }

    const finish = (statePatch = {}) => {
      if (!this._isMounted || seq !== this.requestSeq) {
        return;
      }
      this.setPageLoader(false);
      this.setState({
        loading: false,
        initialized: true,
        ...statePatch,
      });
    };

    try {
      let { pageConfigId, inputVariables, pageConfig } = this.state;
      // Refetch only when page config is missing — empty inputVariables is valid
      // (from_page_mapping may still carry pages_variable_name).
      if (!pageConfigId || !pageConfig) {
        const fetched = await this.fetchPageConfigAndVariables(config.apiAction);
        pageConfigId = fetched.pageConfigId;
        inputVariables = fetched.inputVariables;
        pageConfig = fetched.pageConfig;
      }

      if (!pageConfigId || !pageConfig) {
        finish({ error: "Page Configuration could not be resolved." });
        return;
      }

      const fromMapping = pageConfig.from_page_mapping || [];
      const inputMap = Object.fromEntries(
        (inputVariables || []).map((item) => [item.id, item.api_name])
      );

      const payload = buildLookupExecutePayload({
        fromMapping,
        inputMap,
        config,
        fieldFormPayload: this.getFieldFormPayload(),
        search: search || "",
        page,
        perPage,
      });

      const API_URL = `pageconfig/${pageConfigId}/pages-config-execute`;
      const response = await http.POST(API_URL, payload);

      if (!this._isMounted || seq !== this.requestSeq) {
        return;
      }

      if (response.status !== 200 || !response.data || response.data.code !== 200) {
        finish({
          error: "List data could not be loaded. Please try again.",
          rows: [],
          totalRecords: 0,
        });
        return;
      }

      const responseVariables = response.data.data?.variables || [];
      const extracted = extractListViewExecuteResult({
        responseVariables,
        toPageMapping: pageConfig.to_page_mapping || [],
        resultVar: config.resultVar,
        totalRecordVar: config.totalRecordVar,
      });

      if (!extracted.mapped) {
        finish({
          error: extracted.error || "List result mapping is missing.",
          rows: [],
          totalRecords: 0,
        });
        return;
      }

      finish({
        error: null,
        rows: extracted.rows,
        totalRecords: extracted.totalRecords,
      });
    } catch (e) {
      finish({
        error: "List data could not be loaded. Please try again.",
        rows: [],
        totalRecords: 0,
      });
    }
  };

  recordsChangeHandler = (pagination) => {
    const page = pagination?.page != null ? pagination.page : 0;
    const perPage = pagination?.perPage != null ? pagination.perPage : this.state.perPage;
    let search = pagination?.search != null ? pagination.search : this.state.search;
    if (typeof search === "string") {
      try {
        search = decodeURIComponent(search);
      } catch (e) {
        // keep encoded string
      }
    }
    this.executeList({ page, perPage, search: search || "" }, this.state.config);
  };

  toggleHeaderColumn = () => {
    this.setState((prev) => {
      const opening = !prev.headerColumnOpen;
      return {
        headerColumnOpen: opening,
        draftColumns: opening
          ? prev.columns.map((c) => ({ ...c }))
          : prev.draftColumns,
      };
    }, () => {
      const el = document.getElementById(this.boxId);
      if (el) {
        el.style.display = this.state.headerColumnOpen ? "block" : "none";
      }
    });
  };

  cardBoxCheckAndUncheckHandler = (object) => {
    const cardId = object?.cardId;
    this.setState((prev) => ({
      draftColumns: toggleColumnVisibility(prev.draftColumns, cardId),
    }));
  };

  sortingFieldsHandler = (dropResult) => {
    const { removedIndex, addedIndex } = dropResult || {};
    this.setState((prev) => ({
      draftColumns: reorderColumns(prev.draftColumns, removedIndex, addedIndex),
    }));
  };

  cardBoxCancel = () => {
    this.setState(
      (prev) => ({
        draftColumns: prev.columns.map((c) => ({ ...c })),
        headerColumnOpen: false,
      }),
      () => {
        const el = document.getElementById(this.boxId);
        if (el) el.style.display = "none";
      }
    );
  };

  updateDisplayingHandler = () => {
    const { config, draftColumns, listviewId } = this.state;
    const nextColumns = (draftColumns || []).map((c, i) => ({ ...c, position: i + 1 }));
    const visible = nextColumns.filter((c) => c.isShow);
    const columnsToSave =
      visible.length === 0
        ? nextColumns.map((c) => ({ ...c, isShow: true }))
        : nextColumns;

    if (config?.apiAction) {
      const prefKey = buildListViewColumnsPrefKey(
        resolveListViewUserId(),
        config.apiAction,
        listviewId
      );
      saveListViewColumnPrefs(prefKey, columnsToSave);
    }

    this.setState(
      {
        columns: columnsToSave,
        draftColumns: columnsToSave.map((c) => ({ ...c })),
        headerColumnOpen: false,
      },
      () => {
        const el = document.getElementById(this.boxId);
        if (el) el.style.display = "none";
      }
    );
  };

  retryHandler = () => {
    this.executeList(
      {
        page: this.state.page,
        perPage: this.state.perPage,
        search: this.state.search,
      },
      this.state.config
    );
  };

  getRowActionMarkup = () => {
    const children = this.getChildrenNodes();
    return {
      rowActions: getListViewRowActionNodes(children),
      massActions: getListViewMassActionNodes(children),
    };
  };

  toggleRowSelection = (rowKey, event) => {
    if (event && event.stopPropagation) event.stopPropagation();
    this.setState((prev) => {
      if (prev.isSelectAll) {
        const pageIds = getListViewPageIds(prev.rows, prev.config?.recordIdField);
        return {
          isSelectAll: false,
          selectedIds: pageIds.filter((id) => String(id) !== String(rowKey)),
        };
      }
      return { selectedIds: toggleListViewSelectedId(prev.selectedIds, rowKey) };
    });
  };

  togglePageSelection = (event) => {
    if (event && event.stopPropagation) event.stopPropagation();
    this.setState((prev) => {
      const pageIds = getListViewPageIds(prev.rows, prev.config?.recordIdField);
      const fully = isListViewPageFullySelected(
        pageIds,
        prev.selectedIds,
        prev.isSelectAll
      );
      if (fully) {
        if (prev.isSelectAll) {
          return { isSelectAll: false, selectedIds: [] };
        }
        const remove = new Set(pageIds.map(String));
        return {
          isSelectAll: false,
          selectedIds: prev.selectedIds.filter((id) => !remove.has(String(id))),
        };
      }
      const have = new Set(prev.selectedIds.map(String));
      const next = [...prev.selectedIds];
      pageIds.forEach((id) => {
        if (!have.has(String(id))) next.push(id);
      });
      return { selectedIds: next, isSelectAll: false };
    });
  };

  selectAllMatching = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    this.setState((prev) => ({
      isSelectAll: true,
      selectedIds: getListViewPageIds(prev.rows, prev.config?.recordIdField),
    }));
  };

  clearSelectedRows = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    this.setState({ selectedIds: [], isSelectAll: false });
  };

  renderMarkupButtons = (nodes, loopData) => {
    const convertAndRenderElement = this.props.elmtParams?.convertAndRenderElement;
    if (!nodes || nodes.length === 0 || typeof convertAndRenderElement !== "function") {
      return null;
    }
    return convertAndRenderElement(nodes, {
      ...this.props.elmtParams,
      loopData,
    });
  };

  renderIconAction = (nodes, row, iconType, testId) => {
    if (!nodes || nodes.length === 0) return null;
    const rendered = this.renderMarkupButtons(nodes, row);
    if (!rendered) return null;
    return (
      <span className="scm-listview-icon-action pl-2" data-testid={testId}>
        <Icons iconType={iconType} />
        {rendered}
      </span>
    );
  };

  renderRowMoreMenu = (nodes, row, rowKey) => {
    if (!nodes || nodes.length === 0) return null;
    const rendered = this.renderMarkupButtons(nodes, row);
    if (!rendered) return null;
    return (
      <div className="btn-group dropright hidden-caret d-inline-flex scm-listview-row-more">
        <a
          href="#"
          className="tableGridAction pl-2"
          data-toggle="dropdown"
          aria-expanded="false"
          aria-label="More actions"
          data-testid={`scm-listview-row-more-${rowKey}`}
          onClick={(e) => e.preventDefault()}
        >
          <Icons iconType="EllipsisV" />
        </a>
        <div className="dropdown-menu dropdown-menu-right scm-listview-more-menu">
          {rendered}
        </div>
      </div>
    );
  };

  renderActionHeaderCell = (pageFullySelected) => {
    return (
      <th className="scm-listview-row-actions-cell" data-testid="scm-listview-action-header">
        <label className="scm-listview-check-label">
          <input
            className="form-radio-input m-2"
            type="checkbox"
            name="scm-listview-select-page"
            aria-label="Select all on this page"
            checked={pageFullySelected}
            onChange={this.togglePageSelection}
            onClick={(e) => e.stopPropagation()}
          />
        </label>
      </th>
    );
  };

  renderActionRowCell = (row, rowKey, rowActions, selected) => {
    return (
      <th className="scm-listview-row-actions-cell">
        <label className="scm-listview-check-label">
          <input
            className="form-radio-input m-2"
            type="checkbox"
            name="scm-listview-select-row"
            aria-label={`Select row ${rowKey}`}
            data-testid={`scm-listview-row-check-${rowKey}`}
            checked={selected}
            onChange={(e) => this.toggleRowSelection(rowKey, e)}
            onClick={(e) => e.stopPropagation()}
          />
        </label>
        {this.renderIconAction(rowActions.edit, row, "Edit", `scm-listview-row-edit-${rowKey}`)}
        {this.renderIconAction(
          rowActions.delete,
          row,
          "Delete",
          `scm-listview-row-delete-${rowKey}`
        )}
        {this.renderRowMoreMenu(rowActions.more, row, rowKey)}
      </th>
    );
  };

  renderBulkOverlay = (selectedCount, massActions) => {
    if (selectedCount <= 0) return null;
    const allSelected =
      this.state.isSelectAll || selectedCount === (this.state.totalRecords || 0);
    const massLoopData = buildListViewMassLoopData({
      selectedIds: this.state.selectedIds,
      isSelectAll: this.state.isSelectAll,
      rows: this.state.rows,
      recordIdField: this.state.config?.recordIdField,
      search: this.state.search,
      totalRecords: this.state.totalRecords,
    });
    const massMenu = this.renderMarkupButtons(massActions, massLoopData);

    return (
      <div
        className="action_Btns animated fadeIn"
        data-testid="scm-listview-bulk-bar"
      >
        <p className="mb-0 d-flex flex-wrap">
          <span>
            <span className="badge badge-info" data-testid="scm-listview-selected-count">
              {selectedCount}
            </span>{" "}
            selected{" "}
          </span>
          <span>
            {!allSelected ? (
              <a
                href="#"
                className="text-info textUnderline text-small ml-1"
                data-testid="scm-listview-select-all"
                onClick={this.selectAllMatching}
              >
                Select All{" "}
              </a>
            ) : null}
            <a
              href="#"
              className="text-info textUnderline text-small ml-1"
              data-testid="scm-listview-unselect-all"
              onClick={this.clearSelectedRows}
            >
              Unselect All{" "}
            </a>
          </span>
        </p>
        {massMenu ? (
          <div className="btn-group" data-testid="scm-listview-mass-actions">
            <a
              href="#"
              className="btn border-default dropdown-toggle btn-border dropdown-menu-right hidden-caret"
              data-toggle="dropdown"
              aria-expanded="false"
              aria-label="Mass actions"
              onClick={(e) => e.preventDefault()}
            >
              <Icons iconType="EllipsisH" />
            </a>
            <div className="dropdown-menu dropdown-menu-right scm-listview-mass-menu">
              {massMenu}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  renderCell = (row, col) => {
    const columnKey = col && typeof col === "object" ? col.key : col;
    const fieldType =
      col && typeof col === "object" ? col.type || col.fieldType || "" : "";
    const raw = getValueByPath(row, columnKey);
    const text = formatListViewCellValue(raw, fieldType);
    const kind = normalizeListViewFieldType(fieldType);
    if (isListViewCheckboxFieldType(fieldType)) {
      if (text === "Yes") {
        return (
          <span className="scm-listview-checkbox" title="Selected">
            <i className="fas fa-check text-success fa-lg" aria-label="Yes" />
          </span>
        );
      }
      if (text === "No") {
        return (
          <span className="scm-listview-checkbox" title="Not Selected">
            <i className="fas fa-times text-danger fa-lg" aria-label="No" />
          </span>
        );
      }
      return text;
    }
    if (isListViewCurrencyFieldType(fieldType)) {
      const { amount, code } = parseListViewCurrencyParts(raw);
      if (!amount && !code) return "";
      return (
        <span className="scm-listview-currency">
          {amount ? <span className="scm-listview-currency-amount">{amount}</span> : null}
          {code ? (
            <span className="badge scm-listview-currency-code" title="Currency">
              {code}
            </span>
          ) : null}
        </span>
      );
    }
    if (kind === "imageupload" || kind === "image" || kind === "file") {
      const src = extractListViewMediaUrl(raw) || extractListViewMediaUrl(text);
      if (src && /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(src)) {
        return (
          <span className="scm-listview-file">
            <img src={src} alt="" className="scm-listview-thumb" />
            {text}
          </span>
        );
      }
    }
    return text;
  };

  render() {
    const {
      columns,
      draftColumns,
      rows,
      page,
      perPage,
      search,
      totalRecords,
      loading,
      error,
      initialized,
      selectedIds,
      isSelectAll,
    } = this.state;

    const visibleColumns = (columns || []).filter((c) => c.isShow);
    const paginationProps = {
      page,
      perPage,
      totalRecords,
      search: search ? encodeURIComponent(search) : "",
      recordsChangeHandler: this.recordsChangeHandler,
    };

    const showListChrome = initialized && !error;
    const heading = String(
      this.props.attributes?.title || this.props.attributes?.Title || ""
    ).trim();
    const childrenNodes = this.getChildrenNodes();
    const toolbarNodes = getListViewToolbarNodes(childrenNodes);
    const { rowActions, massActions } = this.getRowActionMarkup();
    const convertAndRenderElement = this.props.elmtParams?.convertAndRenderElement;
    const showToolbar =
      toolbarNodes.length > 0 && typeof convertAndRenderElement === "function";
    const showActionColumn = isListViewActionColumnEnabled(
      this.props.attributes || {},
      rowActions,
      massActions
    );
    const pageIds = getListViewPageIds(rows, this.state.config?.recordIdField);
    const pageFullySelected = isListViewPageFullySelected(
      pageIds,
      selectedIds,
      isSelectAll
    );
    const selectedCount = getListViewSelectedCount(
      selectedIds,
      isSelectAll,
      totalRecords
    );
    const colSpan = Math.max(visibleColumns.length, 1) + (showActionColumn ? 1 : 0);

    return (
      <div
        className="table_Box dataTables_wrapper scm-listview"
        data-testid="scm-listview-root"
        ref={this.rootRef}
      >
        <div className="table_Header d-flex justify-content-between align-items-center flex-wrap">
          {showActionColumn ? this.renderBulkOverlay(selectedCount, massActions) : null}
          {showToolbar ? (
            <div
              className="scm-listview-actions d-flex align-items-center flex-wrap"
              data-testid="scm-listview-actions"
            >
              {convertAndRenderElement(toolbarNodes, this.props.elmtParams)}
            </div>
          ) : heading ? (
            <h2 className="scm-listview-title mb-0" data-testid="scm-listview-title">
              {heading}
            </h2>
          ) : (
            <span />
          )}
          <div className="d-flex align-items-center" style={{ position: "relative" }}>
            <button
              type="button"
              className="btn secondary_bg mr-2"
              title="Header Column"
              onClick={this.toggleHeaderColumn}
            >
              <span className="mr-1" aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                >
                  <path
                    fill="#00117b"
                    d="M16.5,2.333h-15c-0.458,0-0.833,0.375-0.833,0.833v11.667c0,0.458,0.375,0.834,0.833,0.834h15 c0.458,0,0.833-0.376,0.833-0.834V3.167C17.333,2.708,16.958,2.333,16.5,2.333z M5.667,14H2.333V4h3.333V14z M10.667,14H7.333V4 h3.333V14z M15.667,14h-3.334V4h3.334V14z"
                  />
                </svg>
              </span>
              Header Column
            </button>
            <CardBox
              boxId={this.boxId}
              right="0px"
              cardBoxData={columnsToCardBoxData(draftColumns)}
              cardBoxCheckAndUncheckHandler={this.cardBoxCheckAndUncheckHandler}
              sortingFieldsHandler={this.sortingFieldsHandler}
              updateDisplayingHandler={this.updateDisplayingHandler}
              cardBoxCancel={this.cardBoxCancel}
            />
            <SearchRecord {...paginationProps} />
          </div>
        </div>

        {error && !loading ? (
          <div className="table_Body scm-listview-body p-3">
            <div className="text-danger mb-2">{error}</div>
            <button type="button" className="btn btn-border border-default" onClick={this.retryHandler}>
              Retry
            </button>
          </div>
        ) : null}

        {showListChrome ? (
          <div className="table_Body list_View table-scroll-2 scm-listview-body">
            <table className="table table-bordered table-hover mb-0 nowrap">
              <thead>
                <tr>
                  {showActionColumn ? this.renderActionHeaderCell(pageFullySelected) : null}
                  {visibleColumns.map((col) => (
                    <th key={col.key}>{col.label || col.key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      className="dataTables_empty text-center"
                      colSpan={colSpan}
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => {
                    const rowKey = getRowIdentity(
                      row,
                      this.state.config?.recordIdField,
                      index
                    );
                    return (
                      <tr key={rowKey}>
                        {showActionColumn
                          ? this.renderActionRowCell(
                              row,
                              rowKey,
                              rowActions,
                              isListViewRowSelected(rowKey, selectedIds, isSelectAll)
                            )
                          : null}
                        {visibleColumns.map((col) => (
                          <td key={`${rowKey}-${col.key}`}>
                            {this.renderCell(row, col)}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : null}

        {showListChrome ? (
          <div
            className="table_Footer scm-listview-footer"
            data-testid="scm-listview-footer"
          >
            <Pagination {...paginationProps} />
          </div>
        ) : null}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    Loader: (bool) => dispatch(actions.loader(bool)),
  };
};

export default connect(null, mapDispatchToProps)(ScmListViewField);
